<template>
  <div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-12 text-left" :class="{ 'field-error': this.$v.localData.weighing_type_id.$error }">
        <label class="control-label">{{ $t("common.weighing_type") }}</label>
        <DxRadioGroup
          v-model="localData.weighing_type_id"
          :data-source="weighingOnScaleTypesList"
          display-expr="name"
          value-expr="id"
          layout="horizontal"
          @value-changed="onWeighingTypeChanged"
          ref="weighingType"
        />
      </div>
    </div>
    <div v-if="showFieldReportTypes" class="row">
      <div class="form-group col-sm-12 col-lg-12 text-left" :class="{ 'field-error': this.$v.localData.field_report_type?.$error }">
        <label class="control-label">{{ $t("scale.weighing_on_scale.field_report_type") }}</label>
        <DxRadioGroup
          v-model="localData.field_report_type"
          :data-source="fieldReportTypeOptions"
          display-expr="name"
          value-expr="id"
          layout="horizontal"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <div :class="{ 'field-error': this.$v.localData.scale_house_uuid.$error }">
          <label class="control-label">{{ $t("scale.weighing_on_scale.scale_house") }}</label>
          <DxSelectBox
            v-model="localData.scale_house_uuid"
            :data-source="scaleHouses"
            display-expr="name"
            value-expr="uuid"
            :on-value-changed="onScaleHouseChanged"
            placeholder=""
          />
        </div>
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("common.date") }}</label>
        <date-picker2
          v-model="localData.date"
          :value-type="'format'"
          :format="getDefaultDateFormat"
          :placeholder="getDefaultDateFormat"
          type="date"
          :input-class="['date-picker-style']"
          @change="onDateChanged"
          :class="{ 'field-error': this.$v.localData.date.$error }"
          ref="datePicker"
        ></date-picker2>
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <div
          :class="{
            'field-error': this.$v.localData.tractor_number_plate?.$error,
          }"
        >
          <label class="control-label">{{ $t("scale.weighing_on_scale.tractor_number_plate") }}</label>
          <DxAutocomplete
            v-model="localData.tractor_number_plate"
            :data-source="tractorNumberPlateHistory"
            :min-search-length="0"
            :show-clear-button="true"
          />
        </div>
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("scale.weighing_on_scale.trailers_number_plate") }}</label>
        <DxAutocomplete
          v-model="localData.trailer_number_plate"
          :data-source="trailerNumberPlateHistory"
          :min-search-length="0"
          :show-clear-button="true"
        />
      </div>
    </div>
    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <div
          :class="{
            'field-error': this.$v.localData.driver_name?.$error,
          }"
        >
          <label class="control-label">{{ $t("scale.weighing_on_scale.driver_name") }}</label>
          <DxAutocomplete
            v-model="localData.driver_name"
            :data-source="driverNameHistory"
            :min-search-length="0"
            :show-clear-button="true"
            @value-changed="onDriverNameSelected"
          />
        </div>
      </div>
      <div class="row form-group col-sm-12 col-lg-4 text-left">
        <div class="form-group col-sm-6 col-lg-6 text-left">
          <div>
            <label class="control-label">{{ $t("common.serie") }}</label>
            <DxAutocomplete
              v-model="localData.driver_serial_identifier"
              :data-source="driverSerialIdentifierHistory"
              :min-search-length="0"
              :show-clear-button="true"
              @input="onSeriesInput"
            />
          </div>
        </div>
        <div class="form-group col-sm-6 col-lg-6 text-left">
          <div>
            <label class="control-label">{{ $t("common.number") }}</label>
            <DxAutocomplete
              v-model="localData.driver_number_identifier"
              :data-source="driverNumberIdentifierHistory"
              :min-search-length="0"
              :show-clear-button="true"
              @input="onNumberInput"
            />
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div class="form-group col-sm-12 col-lg-4 text-left">
        <div
          :class="{
            'field-error': this.$v.localData.vehicle_configuration?.$error,
          }"
        >
          <label class="control-label">{{ $t("scale.weighing_on_scale.vehicle_configuration") }}</label>
          <DxSelectBox
            v-model="localData.vehicle_configuration"
            :data-source="groupedVehicleConfigurations"
            :grouped="false"
            display-expr="name"
            value-expr="value"
            :group-render="groupTemplate"
            placeholder=""
            :search-enabled="true"
          />
        </div>
      </div>
      <div class="col-sm-4 col-lg-4 form-group">
        <label class="control-label">{{ $t("scale.weighing_on_scale.freight_carrier") }}</label>
        <div class="sm-select-input-dialog-btn" @click="freightCarrierClick">
          <div class="sm-select-input-dialog-info">
            <div class="sm-select-input-dialog-value">{{ getFreightCarrierName }}</div>
          </div>
          <div class="sm-select-input-dialog-icon">
            <i class="fa-regular fa-edit"></i>
          </div>
        </div>
      </div>
    </div>
    <PartnerSelector
      ref="freightCarrierSelector"
      :visible="showFreightCarrierDialog"
      @close="showFreightCarrierDialog = false"
      @continue="onContinueFromFreightCarrierSelector"
      :multiselect="false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { DxSelectBox } from "devextreme-vue/select-box";
import { DxRadioGroup } from "devextreme-vue/radio-group";
import DatePicker2 from "vue2-datepicker";
import required from "vuelidate/src/validators/required";
import requiredIf from "vuelidate/src/validators/requiredIf";
import moment from "moment";
import DxAutocomplete from "devextreme-vue/autocomplete";
import PartnerSelector from "@/components/PartnerSelector.vue";
import { FIELD_REPORT_MODES, WEIGHING_TYPES } from "@/pages/scale/config/weighingTypes";

let weighingTypesPromise = null;

export default {
  name: "settings",
  props: {
    formData: {
      type: Object,
      required: true,
    },
    fieldReportUsaAutomationEnabled: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      localData: { ...this.formData }, // Create a local copy of formData
      weighingOnScaleTypesList: [],
      groupedVehicleConfigurations: [],
      driverNameHistory: [],
      tractorNumberPlateHistory: [],
      trailerNumberPlateHistory: [],
      driverSerialIdentifierHistory: [],
      driverNumberIdentifierHistory: [],
      driverIdentifierRelation: [],
      showFreightCarrierDialog: false,
      loadedScaleHouseUuid: null, // Track which scale_house_uuid has been loaded
    };
  },
  computed: {
    ...mapGetters({
      scaleHouses: "scale/scaleHouses",
      getDefaultDateFormat: "settings/getDefaultDateFormat",
    }),
    showFieldReportTypes() {
      return this.fieldReportUsaAutomationEnabled && parseInt(this.localData.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT;
    },
    fieldReportTypeOptions() {
      return [
        { id: FIELD_REPORT_MODES.SIMPLE, name: this.$t("scale.weighing_on_scale.field_report_simple") },
        {
          id: FIELD_REPORT_MODES.WEIGHING_FOR_TRANSPORT,
          name: this.$t("scale.weighing_on_scale.field_report_weighing_for_transport"),
        },
        { id: FIELD_REPORT_MODES.WITH_SALE, name: this.$t("scale.weighing_on_scale.field_report_with_sale") },
        {
          id: FIELD_REPORT_MODES.WITH_SALE_AND_INTERMEDIARY,
          name: this.$t("scale.weighing_on_scale.field_report_with_sale_intermediary"),
        },
      ];
    },
    getFreightCarrierName() {
      if (!this.localData.freight_carrier_name) {
        return " - ";
      }
      return this.localData.freight_carrier_name;
    },
  },
  components: {
    DxSelectBox,
    DxRadioGroup,
    DatePicker2,
    DxAutocomplete,
    PartnerSelector,
  },
  methods: {
    ...mapActions({
      getScales: "scale/getScales",
      getVehicleConfigurations: "scale/getVehicleConfigurations",
      getWeighingOnScaleTypes: "scale/getWeighingOnScaleTypes",
      getScaleHouses: "scale/getScaleHouses",
      getWeighingOnScaleAutocompleteData: "scale/getWeighingOnScaleAutocompleteData",
    }),
    onDateChanged(date) {},
    freightCarrierClick() {
      this.showFreightCarrierDialog = true;
    },
    onContinueFromFreightCarrierSelector(selectedPartners) {
      const selectedPartner = selectedPartners && selectedPartners.length > 0 ? selectedPartners[0] : null;
      this.localData.freight_carrier_uuid = selectedPartner?.uuid ?? null;
      this.localData.freight_carrier_name = selectedPartner?.name ?? null;

      if (selectedPartner) {
        this.localData.freight_carrier_address = selectedPartner?.locality?.DENUMIRE ?? "";
        if (this.localData.freight_carrier_address && selectedPartner?.street_and_number) {
          this.localData.freight_carrier_address += ", ";
        }
        this.localData.freight_carrier_address += selectedPartner?.street_and_number ?? "";
      } else {
        this.localData.freight_carrier_address = null;
      }

      this.showFreightCarrierDialog = false;
    },
    onScaleHouseChanged(e) {
      if (e.value && this.scaleHouses.length > 0) {
        this.localData.scale_house_name = this.scaleHouses.find((item) => item.uuid === e.value)?.name;

        // Only load scales if uuid changed
        if (e.value !== this.loadedScaleHouseUuid) {
          this.loadedScaleHouseUuid = e.value;
          this.getScales({ scale_house_uuid: e.value });
        }
      }
    },
    onWeighingTypeChanged(e) {
      if (e.value && this.weighingOnScaleTypesList.length > 0) {
        this.localData.weighing_type = this.weighingOnScaleTypesList.find((type) => parseInt(type.id) === parseInt(e.value))?.name;
      }

      this.ensureFieldReportTypeDefaults();
    },
    onSeriesInput(e) {
      const onlyLetters = e.event?.target?.value?.replace(/[^a-zA-Z]/g, "") || "";
      this.localData.driver_serial_identifier = onlyLetters;
      if (e.event && e.event.target) {
        e.event.target.value = onlyLetters;
      }
    },
    onNumberInput(e) {
      const onlyNumbers = e.event?.target?.value?.replace(/[^0-9.]/g, "") || "";
      this.localData.driver_number_identifier = onlyNumbers;
      if (e.event && e.event.target) {
        e.event.target.value = onlyNumbers;
      }
    },
    findIdentifierByDriverName(name) {
      if (!name) return null;
      const relation = this.driverIdentifierRelation.find((r) => r.driver_name === name);
      if (relation) {
        return {
          serial: relation.driver_serial_identifier,
          number: relation.driver_number_identifier,
        };
      }
      return null;
    },
    onDriverNameSelected(e) {
      const driverName = e?.value;
      const identifier = this.findIdentifierByDriverName(driverName);
      if (identifier) {
        this.localData.driver_serial_identifier = identifier.serial || "";
        this.localData.driver_number_identifier = identifier.number || "";
      }
    },
    ensureFieldReportTypeDefaults() {
      if (!this.showFieldReportTypes) {
        if(parseInt(this.localData.weighing_type_id) === WEIGHING_TYPES.FIELD_REPORT) {
          this.localData.field_report_type = FIELD_REPORT_MODES.SIMPLE;
        } else {
          this.localData.field_report_type = null;
        }
        return;
      }

      if (!this.localData.field_report_type) {
        this.localData.field_report_type = FIELD_REPORT_MODES.SIMPLE;
      }
    },
    groupTemplate(groupData) {
      return `<div class="custom-group-header">${groupData.key}</div>`;
    },
    validate() {
      this.$v.$touch(); // Trigger validation
      if (this.$v.$invalid) {
        return {
          error: true,
          message: "",
        };
      }
      return {
        error: false,
        message: "",
      };
    },
    updateData(newData) {
      if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
        // newData.date = moment(newData.date).format(this.getDefaultDateFormat); // Format the date to the default format
        // console.log("updateData step 1 newData.date ", newData.date);
        // console.log("updateData step 1 ", newData);

        this.localData = { ...newData }; // Update localData with new data

        this.ensureFieldReportTypeDefaults();

        if (this.$refs.weighingType) {
          this.$refs.weighingType.instance.option("value", this.localData.weighing_type_id);
        }

        // Only load scales if uuid changed or not yet loaded
        if (this.localData.scale_house_uuid && this.localData.scale_house_uuid !== this.loadedScaleHouseUuid) {
          this.loadedScaleHouseUuid = this.localData.scale_house_uuid;
          this.getScales({ scale_house_uuid: this.localData.scale_house_uuid });
        }
      }
    },
  },
  mounted() {
    // Set default date format if not already set
    if (!this.localData.uuid && !this.localData.date) {
      this.localData.date = moment().format(this.getDefaultDateFormat);
    }

    this.getVehicleConfigurations().then((response) => {
      this.groupedVehicleConfigurations = Object.entries(response).flatMap(([group, items]) =>
        items.map((item) => ({
          group, // Group name (e.g., "Ansamblu vehicule")
          name: item, // Display name
          value: item, // Value for the dropdown
        })),
      );
    });

    if (!weighingTypesPromise) {
      weighingTypesPromise = this.getWeighingOnScaleTypes();
    }

    weighingTypesPromise
      .then((response) => {
        if (response && response.length > 0) {
          this.weighingOnScaleTypesList = response.map((item) => ({
            id: parseInt(item.id),
            name: item.name,
            disabled: parseInt(item.id) == 4 ? true : false,
          }));

          // Set default value for weighing_type_id if not already set
          if (!this.localData.weighing_type_id) {
            this.localData.weighing_type_id = this.weighingOnScaleTypesList[0]?.id; // Default to the first item
            this.localData.weighing_type = this.weighingOnScaleTypesList[0]?.name; // Set the name as well
          }

          this.ensureFieldReportTypeDefaults();
        }
      })
      .catch(() => {
        weighingTypesPromise = null;
      });

    this.getScaleHouses();

    if (this.localData.scale_house_uuid) {
      this.loadedScaleHouseUuid = this.localData.scale_house_uuid;
      this.getScales({ scale_house_uuid: this.localData.scale_house_uuid });
    }

    this.getWeighingOnScaleAutocompleteData({
      history_types: [
        "driver_names",
        "tractor_number_plates",
        "trailer_number_plates",
        "driver_serial_identifiers",
        "driver_number_identifiers",
      ],
    }).then((response) => {
      if (response && response.data) {
        this.driverNameHistory = response.data.driver_names || [];
        this.tractorNumberPlateHistory = response.data.tractor_number_plates || [];
        this.trailerNumberPlateHistory = response.data.trailer_number_plates || [];
        this.driverSerialIdentifierHistory = response.data.driver_serial_identifiers || [];
        this.driverNumberIdentifierHistory = response.data.driver_number_identifiers || [];
        this.driverIdentifierRelation = response.data.driver_identifier_relations || [];
      }
    });
  },
  watch: {
    localData: {
      deep: true,
      handler(newData) {
        this.$emit("update-form-data", newData, 1); // Emit updated data to parent
      },
    },
  },
  validations() {
    return {
      localData: {
        weighing_type_id: {
          required,
        },
        field_report_type: {
          required: requiredIf(() => this.showFieldReportTypes),
        },
        scale_house_uuid: {
          required,
        },
        date: {
          required,
        },
        tractor_number_plate: {
          required,
        },
        vehicle_configuration: {
          required,
        },
        driver_name: {
          required,
        },
      },
    };
  },
};
</script>
<style scoped>
.custom-group-header {
  font-weight: bold;
  font-size: 14px;
  color: #4caf50;
  /* Green color for group headers */
  padding: 5px 10px;
  background-color: #f9f9f9;
  /* Light background for group headers */
  border-bottom: 1px solid #ddd;
}
</style>
